import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import {
  CalendarClock, MoreHorizontal, Phone, MessageSquare, Pencil, ExternalLink, Plus, Navigation, CreditCard,
} from "lucide-react";
import { money, fmtTime, STATUS_TONE, statusLabel, Panel } from "./singleUtils";

const TABS = [
  { id: "upcoming", label: "Upcoming" },
  { id: "in_progress", label: "In Progress" },
  { id: "attention", label: "Needs Attention" },
  { id: "completed", label: "Completed" },
];

const Row = ({ job, onOpen, financialAccess }) => {
  const navigate = useNavigate();
  const time = job.scheduled_time
    ? `${fmtTime(job.scheduled_time)}${job.scheduled_time_end ? `–${fmtTime(job.scheduled_time_end)}` : ""}`
    : "";
  return (
    <div
      className="flex items-center gap-3 border-l-2 border-l-transparent border-b border-border/50 px-2 py-2 hover:border-l-amber-500 hover:bg-amber-500/[0.03] transition-colors cursor-pointer"
      onClick={() => onOpen(job)}
      data-testid={`ops-job-${job.job_id}`}
    >
      <div className="w-[76px] shrink-0">
        {time ? (
          <p className="text-[11px] font-semibold font-mono text-foreground leading-tight">{time}</p>
        ) : (
          <p className="text-[10px] font-mono text-muted-foreground">{(job.scheduled_date || "").slice(5, 10) || "—"}</p>
        )}
        <span className={`inline-block text-[9px] font-semibold uppercase tracking-wide px-1 py-px rounded-sm mt-1 ${STATUS_TONE[job.status] || "bg-slate-500/10 text-slate-500"}`}>
          {statusLabel(job.status)}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{job.title}</p>
        <p className="text-xs text-muted-foreground truncate">
          {job.customer_name}{job.address ? <span className="text-muted-foreground/60"> · {job.address}</span> : null}
        </p>
        <p className="text-[10px] mt-0.5 truncate">
          {job.technician
            ? <span className="text-muted-foreground/80">Tech: {job.technician}</span>
            : <span className="text-amber-600 dark:text-amber-400 font-semibold uppercase tracking-wide">Unassigned</span>}
        </p>
      </div>
      {financialAccess && (job.customer_total > 0 || job.paid > 0) && (
        <div className="text-right shrink-0 hidden sm:block">
          <p className="text-sm font-bold font-mono text-foreground">{money(job.customer_total)}</p>
          {job.balance > 0 && job.paid > 0 ? (
            <p className="text-[10px] font-mono text-orange-500">Paid {money(job.paid)} · Bal {money(job.balance)}</p>
          ) : job.balance > 0 ? (
            <p className="text-[10px] font-mono text-muted-foreground">Balance {money(job.balance)}</p>
          ) : (
            <p className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">PAID</p>
          )}
        </div>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 text-muted-foreground" data-testid={`job-row-actions-${job.job_id}`}>
            <MoreHorizontal className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem onClick={() => onOpen(job)} data-testid="ops-open-job"><ExternalLink className="w-3.5 h-3.5 mr-2" /> Open Job</DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate(`/dashboard/jobs/${job.job_id}/edit`)}><Pencil className="w-3.5 h-3.5 mr-2" /> Edit Job</DropdownMenuItem>
          {financialAccess && job.balance > 0 && (
            <DropdownMenuItem onClick={() => onOpen(job)}><CreditCard className="w-3.5 h-3.5 mr-2" /> Collect Payment</DropdownMenuItem>
          )}
          {job.customer_phone && (
            <DropdownMenuItem onClick={() => { window.location.href = `tel:${job.customer_phone}`; }}>
              <Phone className="w-3.5 h-3.5 mr-2" /> Call Customer
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => navigate("/dashboard/messages")}><MessageSquare className="w-3.5 h-3.5 mr-2" /> Send SMS</DropdownMenuItem>
          {job.address && (
            <DropdownMenuItem onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(job.address)}`, "_blank")}>
              <Navigation className="w-3.5 h-3.5 mr-2" /> Directions
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

const OperationsBoard = ({ operations, onOpenJob, financialAccess }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("upcoming");
  const ops = operations || {};
  const rows = ops[tab] || [];
  return (
    <Panel title="Today's Operations" icon={CalendarClock} testid="todays-operations" bodyClass="px-2 pb-2 pt-2"
      right={
        <button className="text-[11px] font-semibold text-amber-600 dark:text-amber-400 hover:underline" onClick={() => navigate("/dashboard/jobs")} data-testid="ops-all-jobs">
          All Jobs →
        </button>
      }>
      <div className="flex gap-1 mb-1.5 overflow-x-auto px-1">
        {TABS.map((t) => {
          const count = (ops[t.id] || []).length;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-2.5 py-1 rounded-sm text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap transition-colors border-b-2 ${
                tab === t.id ? "border-amber-500 text-foreground bg-amber-500/[0.06]" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`operations-tab-${t.id}`}
            >
              {t.label}{count > 0 && <span className={`ml-1 font-mono ${tab === t.id ? "text-amber-600 dark:text-amber-400" : "opacity-60"}`}>{count}</span>}
            </button>
          );
        })}
      </div>
      {rows.length === 0 ? (
        <div className="text-center py-8" data-testid="ops-empty">
          <p className="text-xs text-muted-foreground mb-3">
            {tab === "upcoming" ? "No jobs scheduled today" : tab === "in_progress" ? "No jobs in progress" : tab === "attention" ? "No jobs need attention" : "No completed jobs in this period"}
          </p>
          {tab === "upcoming" && (
            <Button size="sm" className="bg-amber-500 hover:bg-amber-400 text-black font-semibold" onClick={() => navigate("/dashboard/jobs/new")} data-testid="ops-create-job">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Create Job
            </Button>
          )}
        </div>
      ) : (
        <div className="max-h-[430px] overflow-y-auto">
          {rows.map((j) => <Row key={j.job_id} job={j} onOpen={onOpenJob} financialAccess={financialAccess} />)}
        </div>
      )}
    </Panel>
  );
};

export default OperationsBoard;
